import React, { useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { ChatMessages } from './ChatMessages';
import { ChatInput } from './ChatInput';
import { aiChatService, ChatMessage } from './aiChatService';
import './AiChat.css';

interface ChatPanelProps {
  visible: boolean;
  onClose: () => void;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ visible, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleSend = useCallback(async (message: string) => {
    // Add user message
    const userMsg: ChatMessage = { role: 'user', content: message, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setIsThinking(true);

    let firstToken = true;

    try {
      await aiChatService.sendMessageStream(
        { message, sessionId },
        // onToken
        (token: string) => {
          setIsThinking(false);
          if (firstToken) {
            firstToken = false;
            setMessages((prev) => [...prev, { role: 'assistant', content: token, timestamp: new Date().toISOString() }]);
          } else {
            setMessages((prev) => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              if (last && last.role === 'assistant') {
                updated[updated.length - 1] = { ...last, content: last.content + token };
              }
              return updated;
            });
          }
        },
        // onDone
        (metadata) => {
          setIsThinking(false);
          if (metadata.sessionId) setSessionId(metadata.sessionId);
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === 'assistant') {
              updated[updated.length - 1] = { ...last, sources: metadata.sources, toolCalls: metadata.toolCalls };
            }
            return updated;
          });
        },
        // onError
        (error: string) => {
          setIsThinking(false);
          setMessages((prev) => [...prev, { role: 'assistant', content: error || 'Sorry, I encountered an error.', timestamp: new Date().toISOString() }]);
        },
      );
    } catch (error) {
      setIsThinking(false);
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.', timestamp: new Date().toISOString() }]);
    } finally {
      setIsThinking(false);
    }
  }, [sessionId]);

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(undefined);
  };

  if (!visible) return null;

  return (
    <div className={`ai-chat-panel ${isFullscreen ? 'ai-chat-panel--fullscreen' : ''}`}>
      <div className="ai-chat-panel__header">
        <div className="ai-chat-panel__header-left">
          <span className="ai-chat-panel__title">AI Assistant</span>
        </div>
        <div className="ai-chat-panel__header-actions">
          <Button
            icon="pi pi-plus"
            label="New chat"
            onClick={handleNewChat}
            className="p-button-text p-button-sm ai-chat-panel__new-btn"
          />
          <Button
            icon={isFullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"}
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-button-text p-button-sm"
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          />
          <Button
            icon="pi pi-times"
            onClick={onClose}
            className="p-button-text p-button-sm"
            aria-label="Close"
          />
        </div>
      </div>

      <div className="ai-chat-panel__body">
        {messages.length === 0 ? (
          <div className="ai-chat-panel__welcome">
            <h2 className="ai-chat-panel__greeting">How can I help you today?</h2>
            <div className="ai-chat-panel__prompts">
              <button className="ai-chat-prompt-card" onClick={() => handleSend('Show me failed pipelines')}>
                <span className="ai-chat-prompt-card__title">Pipeline Issues</span>
                <span className="ai-chat-prompt-card__desc">Check failed workflows and get troubleshooting help</span>
              </button>
              <button className="ai-chat-prompt-card" onClick={() => handleSend('What is the current system status?')}>
                <span className="ai-chat-prompt-card__title">System Status</span>
                <span className="ai-chat-prompt-card__desc">Overview of running services and health</span>
              </button>
              <button className="ai-chat-prompt-card" onClick={() => handleSend('How do I onboard a new rApp?')}>
                <span className="ai-chat-prompt-card__title">Onboarding Help</span>
                <span className="ai-chat-prompt-card__desc">Step-by-step guide for onboarding packages</span>
              </button>
            </div>
          </div>
        ) : (
          <ChatMessages messages={messages} isThinking={isThinking} />
        )}
      </div>

      <ChatInput onSend={handleSend} disabled={isThinking} />
    </div>
  );
};
